import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import AxiosInstance from "../utils/axiosInstance";
import { getItem } from "../utils/storage";
import {
  attachSystemColorSchemeListener,
  normalizeAdminThemePreference,
  resolveAdminThemeDark,
} from "../utils/adminThemePreference";

const CustomerDarkModeContext = createContext({
  darkMode: false,
  adminThemePreference: "auto",
  setDarkMode: () => {},
  setAdminThemePreference: () => {},
  toggleDarkMode: () => {},
});

function getCustomerSessionToken() {
  return getItem("customerToken") || null;
}

function persistCustomerTheme(pref, resolvedIsDark) {
  return AxiosInstance.patch("/v1/user/admin-dark-mode", {
    AdminThemePreference: pref,
    IsDarkMode: resolvedIsDark,
  });
}

export function CustomerDarkModeProvider({ children }) {
  const [themePreference, setThemePreferenceState] = useState("auto");
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const themePreferenceRef = useRef(themePreference);
  themePreferenceRef.current = themePreference;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    return attachSystemColorSchemeListener(setSystemPrefersDark);
  }, []);

  const darkMode = useMemo(
    () => resolveAdminThemeDark(themePreference, systemPrefersDark),
    [themePreference, systemPrefersDark]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = getCustomerSessionToken();
      if (!token) return;
      try {
        const res = await AxiosInstance.post("/v1/auth/token_data", { token });
        const ok =
          res?.data?.statusCode === "200" || res?.data?.statusCode === 200;
        if (!ok || cancelled) return;
        const d = res?.data?.data;
        if (!d || d.Role !== "Customer") return;
        const pref = normalizeAdminThemePreference(
          d.IsDarkMode === true,
          d.AdminThemePreference
        );
        if (!cancelled) {
          setThemePreferenceState(pref);
        }
      } catch {
        /* ignore */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const setAdminThemePreference = useCallback((nextPref) => {
    if (nextPref !== "auto" && nextPref !== "light" && nextPref !== "dark") {
      return;
    }
    const prevPref = themePreferenceRef.current;
    if (nextPref === prevPref) return;

    const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const resolved = resolveAdminThemeDark(nextPref, systemDark);

    setThemePreferenceState(nextPref);

    persistCustomerTheme(nextPref, resolved).catch(() => {
      toast.error("Could not save theme preference.");
      setThemePreferenceState(prevPref);
    });
  }, []);

  const setDarkMode = useCallback(
    (value) => {
      const sys =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
      const currentDark = resolveAdminThemeDark(themePreferenceRef.current, sys);
      const nextResolved =
        typeof value === "function" ? Boolean(value(currentDark)) : Boolean(value);
      setAdminThemePreference(nextResolved ? "dark" : "light");
    },
    [setAdminThemePreference]
  );

  const toggleDarkMode = useCallback(() => {
    setAdminThemePreference(darkMode ? "light" : "dark");
  }, [darkMode, setAdminThemePreference]);

  const value = useMemo(
    () => ({
      darkMode,
      adminThemePreference: themePreference,
      setDarkMode,
      setAdminThemePreference,
      toggleDarkMode,
    }),
    [darkMode, themePreference, setDarkMode, setAdminThemePreference, toggleDarkMode]
  );

  return (
    <CustomerDarkModeContext.Provider value={value}>
      {children}
    </CustomerDarkModeContext.Provider>
  );
}

export function useCustomerDarkMode() {
  return useContext(CustomerDarkModeContext);
}
